import { isHubSpotBETPortal } from 'pricing-pages-lib/utils/portalUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import Raven from 'raven-js';
import { ENTERPRISE, STARTER } from 'self-service-api/constants/ProductLevels';
import { ANNUAL } from 'self-service-api/constants/Terms';
import { BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, BUY_TOUCHLESS, REQUEST_ACCESS, TALK_TO_SALES, TALK_TO_SALES_FALLBACK } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { FetchPurchaseMotionRequestTypes } from '../actions/PurchaseMotionActions';
import { isAppAuthSelector, isBillingAdminSelector } from './AuthSelectors';
import { isUngatedForRequestProductButtonSelector, isUngatedForSeatsSelector } from './GatingSelectors';
import { hasPurchaseMotionsSucceededSelector } from './LoadingStateSelectors';
import { crmSuiteOwnershipLevel } from './ProductInventorySelectors';
import { crmSuiteProductLevelsSelector, isMarketingContactsQuantityPackSelector, isQuantityUpgradeSelector, productForSkuIdSelector } from './ProductsSelectors';
import { makeRequestStatusSelector } from './RequestStatusSelectors';
import { isDevPortalSelector } from './ScopeSelectors';
import { canUpgradeToProductLevelSuiteSelector } from './UpgradeSelectors';
export const fetchPurchaseMotionStatus = makeRequestStatusSelector(FetchPurchaseMotionRequestTypes.REQUEST);
export const allPurchaseMotionsSelector = state => {
  return state.purchaseMotionsByLocation;
};
export const talkToSalesTypeSelector = state => {
  try {
    const allPurchaseMotions = allPurchaseMotionsSelector(state);
    if (allPurchaseMotions) {
      if (Object.keys(allPurchaseMotions).length === 0) {
        return TALK_TO_SALES_FALLBACK;
      }
      for (const location of Object.keys(allPurchaseMotions)) {
        var _allPurchaseMotions$l;
        const purchaseMotionsForLocation = (_allPurchaseMotions$l = allPurchaseMotions[location]) === null || _allPurchaseMotions$l === void 0 ? void 0 : _allPurchaseMotions$l.purchaseMotions;
        if (!purchaseMotionsForLocation) continue;
        for (const apiName of Object.keys(purchaseMotionsForLocation)) {
          const purchaseMotionsObjectArray = purchaseMotionsForLocation[apiName] || [];
          const purchaseMotions = Array.isArray(purchaseMotionsObjectArray) ? purchaseMotionsObjectArray.map(({
            key
          }) => key) : [];
          if (purchaseMotions.includes(TALK_TO_SALES)) {
            return TALK_TO_SALES;
          }
          if (purchaseMotions.includes(TALK_TO_SALES_FALLBACK)) {
            return TALK_TO_SALES_FALLBACK;
          }
        }
      }
    }
  } catch (error) {
    Raven.captureException(new Error('Error accessing purchase motions', {
      cause: error
    }));
  }
  return TALK_TO_SALES_FALLBACK;
};

// For Seats Marketing Hub, it's possible to have 2 touchless purchase motions -
// Buy Additional Contacts + Buy Additional Seats. For enterprise, contacts are
// not available for touchless purchase. However, it's possible to still add
// contacts via the calculator. The Calculator then sees Buy Additional Seats
// as a touchless purchase motion, so the customer can attempt to go to the
// interstitial for touchless checkout.
export const canExplicitlyBuyMarketingContacts = (state, {
  skuId,
  location,
  purchaseMotions
}) => {
  const isUngatedForSeats = isUngatedForSeatsSelector(state);
  const isMarketingContactsQuantityPack = isMarketingContactsQuantityPackSelector(state, {
    skuId
  });
  // Still want to display Buy Additional Seats on the merch card, not TTS
  if (!isUngatedForSeats || !isMarketingContactsQuantityPack || location === 'MERCH_CARD') return true;
  return Boolean(purchaseMotions.find(purchaseMotion => [BUY_TOUCHLESS, BUY_ADDITIONAL_CONTACTS].includes(purchaseMotion)));
};
const getPurchaseMotionsSafely = (allPurchaseMotions, location, apiName) => {
  try {
    var _allPurchaseMotions$l2;
    if (apiName && allPurchaseMotions !== null && allPurchaseMotions !== void 0 && (_allPurchaseMotions$l2 = allPurchaseMotions[location]) !== null && _allPurchaseMotions$l2 !== void 0 && (_allPurchaseMotions$l2 = _allPurchaseMotions$l2.purchaseMotions) !== null && _allPurchaseMotions$l2 !== void 0 && _allPurchaseMotions$l2[apiName]) {
      return allPurchaseMotions[location].purchaseMotions[apiName] || [];
    }
    return [];
  } catch (error) {
    Raven.captureException(new Error('Error accessing purchase motions', {
      cause: error
    }));
    return [];
  }
};
export const purchaseMotionsSelector = (state, {
  location = 'MERCH_CARD',
  skuId
}) => {
  const {
    apiName
  } = productForSkuIdSelector(state, {
    skuId
  });
  const isMarketingContactsQuantityPack = isMarketingContactsQuantityPackSelector(state, {
    skuId
  });
  const isUngatedForSeats = isUngatedForSeatsSelector(state);
  const allPurchaseMotions = allPurchaseMotionsSelector(state);
  let purchaseMotions = getPurchaseMotionsSafely(allPurchaseMotions, location, apiName);
  if (isDevPortalSelector(state)) {
    purchaseMotions = purchaseMotions.filter(({
      key
    }) => key !== BUY_TOUCHLESS);
  }
  const originalPurchaseMotions = purchaseMotions.map(({
    key
  }) => key);
  if (isUngatedForSeats && isMarketingContactsQuantityPack) {
    const canBuyContactsExplicitly = canExplicitlyBuyMarketingContacts(state, {
      skuId,
      location,
      purchaseMotions: originalPurchaseMotions
    });
    if (canBuyContactsExplicitly) {
      return originalPurchaseMotions;
    } else {
      return [talkToSalesTypeSelector(state)];
    }
  }
  if (!originalPurchaseMotions.length) {
    return [talkToSalesTypeSelector(state)];
  }
  return originalPurchaseMotions;
};
const hasNoPurchaseMotionsSelector = state => {
  const purchaseMotions = allPurchaseMotionsSelector(state);
  const hasPurchaseMotionsSucceeded = hasPurchaseMotionsSucceededSelector(state);
  return hasPurchaseMotionsSucceeded && Object.keys(purchaseMotions).length === 0;
};
const TOUCHLESS_PURCHASE_MOTION_VARIANTS = [BUY_TOUCHLESS, BUY_ADDITIONAL_SEATS, BUY_ADDITIONAL_CONTACTS];
export const purchaseMotionsContainTouchlessVariant = purchaseMotions => {
  return purchaseMotions.some(purchaseMotion => TOUCHLESS_PURCHASE_MOTION_VARIANTS.includes(purchaseMotion));
};
export const skuIdsContainTouchlessPurchaseMotionVariants = (state, {
  skuIds,
  location
}) => {
  return skuIds.every(skuId => {
    const purchaseMotions = purchaseMotionsSelector(state, {
      skuId,
      location
    });
    return purchaseMotionsContainTouchlessVariant(purchaseMotions);
  });
};

// We can send quantity upgrades that don't have matching purchase motions
// (buyAdditionalSeats and buyAdditionalContacts) together to Shopping
export const skuIdsAreQuantityUpgradeMixedTouchlessPurchase = (state, {
  skuIds,
  location = 'CALCULATOR'
}) => {
  const allTouchlessVariants = skuIdsContainTouchlessPurchaseMotionVariants(state, {
    skuIds,
    location
  });
  if (!allTouchlessVariants) return false;
  const productConfigurations = skuIds.map(skuId => createProductConfig({
    skuId
  }));
  return isQuantityUpgradeSelector(state, {
    productConfigurations
  });
};
export const canSkuIdsBePurchasedTouchlesslyTogether = (state, {
  skuIds,
  location = 'MERCH_CARD'
}) => {
  const isUpgradeable = skuIdsContainTouchlessPurchaseMotionVariants(state, {
    skuIds,
    location
  });
  const isQuantityUpgrade = skuIdsAreQuantityUpgradeMixedTouchlessPurchase(state, {
    skuIds,
    location
  });
  return isUpgradeable || isQuantityUpgrade;
};
export const commonPurchaseMotionsSelector = (state, {
  skuIds,
  location,
  queryParamTerm = ANNUAL
}) => {
  const foundPurchaseMotions = {};
  skuIds.forEach(skuId => purchaseMotionsSelector(state, {
    location,
    skuId
  }).forEach(purchaseMotion => {
    if (purchaseMotion in foundPurchaseMotions) {
      foundPurchaseMotions[purchaseMotion]++;
    } else {
      foundPurchaseMotions[purchaseMotion] = 1;
    }
  }));
  let commonPurchaseMotions = Object.keys(foundPurchaseMotions).filter(purchaseMotion => foundPurchaseMotions[purchaseMotion] === skuIds.length);
  const hasNoPurchaseMotions = hasNoPurchaseMotionsSelector(state);
  const isMixedTouchlessPurchase = isUngatedForSeatsSelector(state) && canSkuIdsBePurchasedTouchlesslyTogether(state, {
    skuIds,
    location,
    queryParamTerm
  });
  if (isMixedTouchlessPurchase && !purchaseMotionsContainTouchlessVariant(commonPurchaseMotions)) {
    commonPurchaseMotions = [BUY_TOUCHLESS, ...commonPurchaseMotions];
  }
  if (!commonPurchaseMotions.length && !hasNoPurchaseMotions) {
    return [talkToSalesTypeSelector(state)];
  }
  return commonPurchaseMotions;
};
export const suitePurchaseMotionsMapSelector = state => {
  const hasNoPurchaseMotions = hasNoPurchaseMotionsSelector(state);
  if (hasNoPurchaseMotions) {
    return null;
  }
  const isUngatedForSeats = isUngatedForSeatsSelector(state);
  const isAdmin = isBillingAdminSelector(state);
  const ownedSuiteLevel = crmSuiteOwnershipLevel(state);
  const suiteProductLevels = crmSuiteProductLevelsSelector(state);
  const isUngatedForRequestProductButton = isUngatedForRequestProductButtonSelector(state);
  const isAppAuth = isAppAuthSelector(state);
  return suiteProductLevels.reduce((acc, productLevel) => {
    if (isUngatedForSeats && productLevel === ownedSuiteLevel) {
      let purchaseMotionsList;
      if (productLevel === ENTERPRISE) {
        purchaseMotionsList = [BUY_ADDITIONAL_SEATS];
      } else {
        purchaseMotionsList = [BUY_ADDITIONAL_SEATS, BUY_ADDITIONAL_CONTACTS];
      }
      return Object.assign({}, acc, {
        [ownedSuiteLevel]: purchaseMotionsList
      });
    }
    const canUpgradeToProductLevelSuite = canUpgradeToProductLevelSuiteSelector(state, {
      productLevel
    });
    const ttsType = talkToSalesTypeSelector(state);
    if (!canUpgradeToProductLevelSuite || productLevel !== STARTER) {
      return Object.assign({}, acc, {
        [productLevel]: [ttsType]
      });
    }
    return Object.assign({}, acc, {
      [productLevel]: !isAdmin && isAppAuth && isUngatedForRequestProductButton ? [REQUEST_ACCESS, ttsType] : [BUY_TOUCHLESS, ttsType]
    });
  }, {});
};
export const isRussianBlockedSelector = state => {
  const hasNoPurchaseMotions = hasNoPurchaseMotionsSelector(state);
  const isHSPortal = isHubSpotBETPortal();
  return hasNoPurchaseMotions && !isHSPortal;
};